import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { getApiCall, postApiCall } from "../../../service/httpCall";
import {
  API_WEBHOOK_LIST,
  API_WEBHOOK_DELETE,
  API_WEBHOOK_GET_ONE,
} from "../../../service/url";
import { useAuth, errorCheck } from "../auth/auth-utils";
import { ROUTES, USER_ROLES, MODULE_ACL } from "../../../utilities/constant";
import { exportToCSV, hasPermission } from '../../../utilities/helpers';
import Loader from "../../../components/partials/components/loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import "../../../assets/custom/css/custom.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import { getUser } from "../../../utilities/setting";
import { toast } from "react-toastify";
import exportData from '../../../utilities/export';

const ManageWebhookList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [searchValue, setSearchValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [webhooksData, setWebhooksData] = useState([]);
  const [showHeadersModal, setShowHeadersModal] = useState(false);
  const [showBodyModal, setShowBodyModal] = useState(false);
  const [selectedHeaders, setSelectedHeaders] = useState("");
  const [selectedBody, setSelectedBody] = useState("");
   const [loggedInUserACL, setLoggedInUserACL] = useState({})
   const [isSuperAdmin, setIsSuperadmin] = useState(false)
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    webhook_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    url: { value: null, matchMode: FilterMatchMode.CONTAINS },
    event_id: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const handleExport = async () => {
   try {
      setButtonDisable(true);

      exportToCSV({
         fileName: "webhook-list",
         data: formattedData,
         columns: [
            {
               header: "Webhook Id",
               accessor: "id"
            },
            {
               header: "Webhook Name",
               accessor: (row) =>
                  row.webhook_name || "-"
            },
            {
               header: "Webhook Url",
               accessor: (row) =>
                  row.url || "-"
            },
            {
               header: "Webhook Event",
               accessor: (row) =>
                  Array.isArray(row.event_name)
                     ? row.event_name.join(", ")
                     : row.event_name || "-"
            },
            {
               header: "Created At",
               accessor: (row) =>
                  formatTimeContent(row)
            },
            {
               header: "Updated At",
               accessor: (row) =>
                  formatTimeContent({
                     ...row,
                     created_at: row.updated_at
                  })
            },
            {
               header: "Headers",
               accessor: (row) =>
                  row.headers
                     ? JSON.stringify(row.headers)
                     : "-"
            },
            {
               header: "Body",
               accessor: (row) =>
                  row.body
                     ? JSON.stringify(row.body)
                     : "-"
            }
         ]
      });

   } finally {
      setButtonDisable(false);
   }
};

const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  useEffect(() => {
    const user = getUser();
      if (user) {
        if (user?.acl_settings) {    
          setModuleOperationsForReadPermissionCheck(JSON.parse(user.acl_settings.module_operations));
        }
      }
  }, []);

  // Fetch list
  useEffect(() => {
    setLoading(true);
      const user = getUser();
         if (user.role === USER_ROLES.SUPERADMIN) {
            setIsSuperadmin(true);
         }
         if (user?.acl_settings) {
            setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
         }
    getApiCall(API_WEBHOOK_LIST, {})
      .then(onListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  const onListSuccess = (resp) => {
    setWebhooksData(resp || []);
  };

  const onError = (err) => {
    if (typeof err !== "object" && err.includes("TokenExpiredError")) {
      errorCheck(err, navigate, null, null);
      toast.error(err);
    } else {
      console.log(err);
    }
  };

  // Delete webhook
  const confirmDelete = () => {
    postApiCall(API_WEBHOOK_DELETE, { id: webhookToDelete })
      .then(onDeleteSuccess)
      .catch(onDeleteError);
    setShowDeleteModal(false);
    setWebhookToDelete(null);
  };

  const onDeleteSuccess = (resp) => {
    toast.success("Webhook deleted successfully");
    getApiCall(API_WEBHOOK_LIST, {}).then(onListSuccess).catch(onError);
  };

  const onDeleteError = (err) => {
    toast.error(typeof err === "string" ? err : JSON.stringify(err));
  };

  // View Headers
  const handleViewHeaders = (headerJson) => {
    try {
      const parsed = typeof headerJson === "string" ? JSON.parse(headerJson) : headerJson;
      setSelectedHeaders(JSON.stringify(parsed, null, 2));
      setShowHeadersModal(true);
    } catch (e) {
      setSelectedHeaders(headerJson || "{}");
      setShowHeadersModal(true);
    }
  };

  // View Body
  const handleViewBody = (bodyJson) => {
    try {
      const parsed = typeof bodyJson === "string" ? JSON.parse(bodyJson) : bodyJson;
      setSelectedBody(JSON.stringify(parsed, null, 2));
      setShowBodyModal(true);
    } catch (e) {
      setSelectedBody(bodyJson || "{}");
      setShowBodyModal(true);
    }
  };

  // Handle actions
  const handleAction = (action, id) => {
    if (action === "edit") {
      navigate(`/${ROUTES.MANAGE_WEBHOOK.EDIT.replace(":id", id)}`);
    } else if (action === "delete") {
      setWebhookToDelete(id);
      setShowDeleteModal(true);
    }
  };

  // Update filters when search value changes
  // useEffect(() => {
  //   setFilters({
  //     global: { value: searchValue, matchMode: FilterMatchMode.CONTAINS },
  //   });
  // }, [searchValue]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      global: { value: searchValue, matchMode: FilterMatchMode.CONTAINS },
    }));
  }, [searchValue]);

  const getEventName = (eventId) => {
    if (eventId == 1) return "HANGUP";
    if (eventId == 2) return "DTMF";
    if (eventId == 3) return "CONNECTED_CALLS";
    return "UNKNOWN";
  };

  // Action column template
  const actionBodyTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
         {(isSuperAdmin || loggedInUserACL[MODULE_ACL.WEBHOOK]?.edit) && (
          <FiEdit
            style={{ color: "#007bff", cursor: "pointer", fontSize: "18px" }}
            onClick={() => handleAction("edit", rowData.id)}
          />
         )}

         {(isSuperAdmin || loggedInUserACL[MODULE_ACL.WEBHOOK]?.delete) && (
          <FiTrash2
            style={{ color: "#dc3545", cursor: "pointer", fontSize: "18px" }}
            onClick={() => handleAction("delete", rowData.id)}
          />
         )}

      </div>
    );
  };

  // Headers column template
  const headersBodyTemplate = (rowData) => {
    return (
      <Button
        variant="primary"
        size="sm"
        onClick={() => handleViewHeaders(rowData.header_json)}
        className="view-btn-hover"
      >
        View
      </Button>
    );
  };

  // Body column template
  const bodyBodyTemplate = (rowData) => {
    return (
      <Button
        variant="primary"
        size="sm"
        onClick={() => handleViewBody(rowData.body_json)}
        className="view-btn-hover"
      >
        View
      </Button>
    );
  };

  // Event column template
  const eventBodyTemplate = (rowData) => {
    return getEventName(rowData.event_id);
  };

  const formatTimeContent = (rowData, column) => {
    const value = rowData[column.field];

    if (!value) return null;

    const date = new Date(value);

    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
    });
  };

  const formattedData = webhooksData.map((item) => ({
    ...item,
    event_name: getEventName(item.event_id),
  }));

  const canRead = hasPermission(
       moduleOperationsForReadPermissionCheck,
       "Webhook",
       "read",
     );
   
     if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
       return (
         <div
           className="text-center p-4"
           style={{
             marginTop: "50px",
             marginBottom: "50px",
             fontSize: "16px",
           }}
         >
           No read permission found in assigned ACL
         </div>
       );
     }

  if (loading) return <Loader />;

  return (
    <>
      <style>
        {`
          .view-btn-hover {
            transition: all 0.2s ease !important;
          }
          .view-btn-hover:hover {
            background-color: #0056b3 !important;
            transform: scale(1.05);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
        `}
      </style>
      <div style={{ marginTop: "20px" }}>
        <Row>
          <Col sm="12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="header-title px-4 py-3 d-flex justify-content-between align-items-center">
                  <h4 className="card-title m-0">
                    Webhooks ({webhooksData.length})
                  </h4>
                  <div className="d-flex gap-3">
                    {(isSuperAdmin || loggedInUserACL[MODULE_ACL.WEBHOOK]?.add) && (
                      <Button
                        variant="primary"
                        className="d-flex align-items-center"
                        onClick={() => navigate(`/${ROUTES.MANAGE_WEBHOOK.CREATE}`)}
                      >
                        <FiPlus className="me-2" />
                        Create Webhook
                      </Button>
                    )}
                    {webhooksData.length > 0 && (
                      <Button
                        label="Export Data"
                        icon="pi pi-file-export"
                        onClick={handleExport}
                        className="p-button-active"
                        style={{ paddingLeft: "20px" }}
                        disabled={buttonDisable}
                      >
                        Export Data &nbsp;
                        {buttonDisable && (
                          <span
                            className="spinner-border spinner-border-sm ml-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3">
                  <div className="table-responsive border-bottom my-3">
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        marginBottom: "10px",
                        paddingLeft: "10px",
                      }}
                    >
                      <Form.Control
                        style={{
                          height: "50px",
                          marginLeft: "10px",
                          border: "1px solid #ced4da",
                          fontSize: "15px",
                          maxWidth: "300px",
                        }}
                        placeholder="Search by Name, Event or URL..."
                        type="text"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setSearchValue("")}
                        style={{
                          marginLeft: "10px",
                          height: "50px",
                        }}
                        disabled={!searchValue.trim()}
                      >
                        Clear Filters
                      </Button>
                    </div>
                    </div>

                    <DataTable
                      value={formattedData}
                      tableStyle={{ minWidth: "50rem" }}
                      filters={filters}
                      globalFilterFields={["id", "webhook_name", "url", "event_name"]}
                      paginator
                      rows={10}
                      emptyMessage="No webhooks found"
                    >
                      <Column
                        field="id"
                        filterField="id"
                        header="Webhook Id"
                        filter
                        filterPlaceholder="Search by ID"
                      />
                      <Column
                        field="webhook_name"
                        filterField="webhook_name"
                        header="Webhook Name"
                        filter
                        filterPlaceholder="Search by name"
                        body={(rowData) => rowData.webhook_name || "-"}
                      />
                      <Column
                        field="url"
                        filterField="url"
                        header="Webhook Url"
                        filter
                        filterPlaceholder="Search by URL"
                        body={(rowData) => rowData.url || "-"}
                      />
                      <Column
                        field="event_name"
                        header="Webhook Event"
                        body={eventBodyTemplate}
                        filterPlaceholder="Search by Event"
                      />
                      <Column
                        field="created_at"
                        header="Created At"
                        body={formatTimeContent}
                      />
                      <Column
                        field="updated_at"
                        header="Updated At"
                        body={formatTimeContent}
                      />
                      <Column header="Headers" body={headersBodyTemplate} />
                      <Column header="Body" body={bodyBodyTemplate} />
                      <Column header="Action" body={actionBodyTemplate} />
                    </DataTable>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Headers View Modal */}
      <Modal show={showHeadersModal} onHide={() => setShowHeadersModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Headers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "5px",
              overflow: "auto",
              maxHeight: "400px",
              fontFamily: "monospace",
              fontSize: "12px",
            }}
          >
            {selectedHeaders}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHeadersModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Body View Modal */}
      <Modal show={showBodyModal} onHide={() => setShowBodyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Body</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "5px",
              overflow: "auto",
              maxHeight: "400px",
              fontFamily: "monospace",
              fontSize: "12px",
            }}
          >
            {selectedBody}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBodyModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this webhook? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ManageWebhookList;
